<template>
  <div class="cart-sidebar shadow-lg rounded-4 overflow-hidden border-0 card">

    <!-- Header -->
    <div class="cart-header p-3 bg-dark text-white d-flex justify-content-between align-items-center">
      <h5 class="m-0">SAVATCHA</h5>
      <div class="d-flex gap-2">
        <button v-if="hasServices" @click="$emit('clear-services')" class="btn btn-sm btn-outline-warning border-0" title="Xizmatlarni tozalash">Xizmatlar ×</button>
        <button @click="$emit('clear-all')" class="btn btn-sm btn-outline-light border-0">Hammasi ×</button>
      </div>
    </div>

    <!-- Savatcha ro'yxati -->
    <div class="cart-items p-2 overflow-auto" style="max-height: 35vh;">
      <div v-if="cart.length === 0" class="text-center py-4">
        <p class="text-muted small mb-0">Savatcha bo'sh</p>
      </div>

      <div v-for="(item, index) in cart" :key="index" class="cart-row mb-2"
           :class="[item.type === 'service' ? 'service-row' : '', item.from_service ? 'service-product-row' : '']">

        <!-- Xizmatga tegishli mahsulot (kichik ko'rinishda) -->
        <template v-if="item.from_service">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="small text-muted d-block text-truncate" style="max-width:165px">↳ {{ item.name }}</span>
              <span class="badge bg-info" style="font-size:0.55rem">Material</span>
            </div>
            <span class="small text-muted">{{ item.qty }} x {{ formatSumma(item.price) }} = {{ formatSumma(item.price * item.qty) }}</span>
          </div>
        </template>

        <!-- Oddiy mahsulot yoki xizmat -->
        <template v-else>
          <div class="d-flex justify-content-between align-items-start mb-1">
            <div>
              <span class="small fw-bold d-block text-truncate" style="max-width:165px">{{ item.name }}</span>
              <span :class="['badge', item.type === 'service' ? 'bg-purple' : 'bg-secondary']" style="font-size:0.6rem">
                {{ item.type === 'service' ? 'Xizmat' : 'Mahsulot' }}
              </span>
            </div>
            <button @click="$emit('remove-item', index)" class="btn-close-sm">&times;</button>
          </div>

          <!-- Xizmat uchun narx ko'rinishi -->
          <div v-if="item.type === 'service'" class="mb-1">
            <small class="text-muted d-block">
              {{ saleType === 'optom' ? 'Optom' : 'Chakana' }}:
              {{ formatSumma(serviceItemPrice(item)) }} so'm/ta
            </small>
          </div>

          <div class="d-flex justify-content-between align-items-center">
            <div class="quantity-control">
              <button @click="$emit('decrease-qty', item)" class="qty-btn">-</button>
              <span class="qty-val">{{ item.qty }}</span>
              <button @click="$emit('increase-qty', item)" class="qty-btn">+</button>
            </div>
            <span class="fw-bold text-primary small">
              {{ item.type === 'service'
                 ? formatSumma(serviceItemPrice(item) * item.qty)
                 : formatSumma(item.price * item.qty) }} so'm
            </span>
          </div>

          <!-- Erkin narx (faqat mahsulot uchun) — switch bilan boshqariladi -->
          <div v-if="item.type === 'product'" class="price-edit-row mt-1">
            <label class="price-switch" :title="item.price_unlocked ? 'Narxni qulflash' : 'Erkin narx'">
              <input
                type="checkbox"
                :checked="!!item.price_unlocked"
                @change="onTogglePriceLock(item, $event)"
              />
              <span class="ps-slider"></span>
            </label>
            <span class="text-muted small">Erkin narx:</span>
            <input
              type="number"
              min="0"
              :value="item.price"
              :disabled="!item.price_unlocked"
              @input="onPriceEdit(item, $event)"
              :class="['price-input', !item.price_unlocked ? 'locked' : '']"
            />
            <span
              v-if="Number(item.original_price || 0) > 0 && Number(item.price) < Number(item.original_price)"
              class="price-original"
              :title="'Asl narx: ' + formatSumma(item.original_price)"
            >{{ formatSumma(item.original_price) }}</span>
            <span
              v-else-if="Number(item.original_price || 0) > 0 && Number(item.price) > Number(item.original_price)"
              class="price-expensive"
              :title="'Qimmat sotildi (asl: ' + formatSumma(item.original_price) + ')'"
            >+{{ formatSumma(Number(item.price) - Number(item.original_price)) }}</span>
          </div>

          <!-- Per-product chegirma foizi (faqat mahsulot uchun) -->
          <div v-if="item.type === 'product'" class="discount-pct-row mt-1">
            <span class="dp-icon" title="Chegirma foizi (asl narxdan)">%</span>
            <input
              type="number"
              min="0"
              max="100"
              step="0.5"
              :value="item.discount_percent || 0"
              @input="onDiscountPercentEdit(item, $event)"
              class="dp-input"
              placeholder="0"
            />
            <span class="text-muted" style="font-size:11px">% chegirma</span>
            <span v-if="Number(item.discount_percent) > 0 && Number(item.original_price) > 0" class="dp-saved" :title="'Tejaldi'">
              −{{ formatSumma(Number(item.original_price) * Number(item.discount_percent) / 100 * Number(item.qty)) }}
            </span>
          </div>
        </template>
      </div>
    </div>

    <!-- Summary va To'lov -->
    <div class="cart-summary p-3 bg-white border-top">

      <!-- Summa detallari -->
      <div class="summary-details">
        <div v-if="hasServices" class="d-flex justify-content-between mb-1 align-items-center">
          <span class="text-muted small">Xizmatlar</span>
          <span class="fw-bold small text-purple">{{ formatSumma(serviceSumma) }}</span>
        </div>
        <div v-if="hasProducts" class="d-flex justify-content-between mb-1 align-items-center">
          <span class="text-muted small">Mahsulotlar</span>
          <span class="fw-bold small">{{ formatSumma(productSumma) }}</span>
        </div>
        <div class="d-flex justify-content-between mb-1 align-items-center">
          <span class="text-muted small">Chegirma</span>
          <input type="number" :value="form.skidka_summa" @input="updateForm('skidka_summa', $event)" class="summary-input-field text-danger">
        </div>
        <div v-if="form.master_user_id" class="d-flex justify-content-between mb-1 align-items-center">
          <span class="text-muted small">Usta ({{ masterPercent }}%)</span>
          <span class="fw-bold small text-info">{{ formatSumma(masterSumma) }}</span>
        </div>
        <div class="d-flex justify-content-between mt-2 pt-2 border-top">
          <h5 class="fw-bolder m-0">JAMI:</h5>
          <h5 class="fw-bolder m-0 text-dark">{{ formatSumma(totalSum) }}</h5>
        </div>
      </div>

      <!-- To'lov qismi — har pay_type uchun alohida input, qoldiq avtomatik qarz -->
      <div class="payment-section mt-3">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <label class="small text-muted fw-bold m-0">TO'LOV TURLARI</label>
          <button type="button" class="quick-btn" @click="autofillCash" :title="'Hammasini Naqd ga toʼldir'">
            ⚡ Hammasi Naqdga
          </button>
        </div>

        <!-- Multi-payment grid: har pay_type uchun input -->
        <div v-if="totalSum > 0" class="multi-pay-grid">
          <div
            v-for="pt in payTypes"
            :key="pt.id"
            class="mp-row"
            :class="{ 'mp-dollar': pt.id === dollarPayTypeId, 'mp-active': (paymentByType[pt.id] || 0) > 0 }"
          >
            <span class="mp-icon">{{ payTypeIcon(pt.id, pt.name) }}</span>
            <div class="mp-info">
              <span class="mp-name">{{ pt.name }}</span>
              <small class="mp-unit">{{ pt.id === dollarPayTypeId ? 'USD' : "so'm" }}</small>
            </div>
            <input
              type="number"
              min="0"
              :step="pt.id === dollarPayTypeId ? 50 : 1000"
              :value="paymentByType[pt.id] || 0"
              @input="onPaymentChange(pt.id, $event.target.value)"
              class="mp-input"
              placeholder="0"
            />
            <button
              type="button"
              class="mp-fill"
              @click="fillRemaining(pt.id)"
              :title="'Qoldiqni shu turga toʼldir'"
            >Qoldiq</button>
          </div>
        </div>

        <!-- Status: jami va qarz -->
        <div v-if="totalSum > 0" class="multi-pay-status">
          <div class="mps-row">
            <span>Jami to'lanmoqda:</span>
            <strong class="text-success">{{ formatSumma(totalPaidSum) }} so'm</strong>
          </div>
          <div class="mps-row" v-if="dollarTotal > 0">
            <span>Dollar (kursi {{ formatSumma(currentRate) }}):</span>
            <strong class="text-info">{{ dollarTotal }} $ ≈ {{ formatSumma(dollarInSum) }} so'm</strong>
          </div>
          <div class="mps-row mps-debt" :class="{ 'mps-overpay': overpaid }">
            <span>{{ overpaid ? "Ortiqcha to'landi:" : "Qarzga qoladi:" }}</span>
            <strong>{{ formatSumma(Math.abs(qarzAmount)) }} so'm</strong>
          </div>
          <div class="mps-row mps-skip" v-if="qarzAmount === 0 && totalPaidSum > 0">
            <span>✅ To'liq to'landi</span>
          </div>
        </div>

        <!-- QARZ TURI — qarzdan qancha qismi $ da, qancha so'mda -->
        <div v-if="qarzAmount > 0 && currentRate > 1" class="qarz-split-block mt-3">
          <div class="qs-header">
            <span>💱 QARZ TURI</span>
            <small>Kursi: {{ formatSumma(currentRate) }}</small>
          </div>

          <!-- Tezkor yumaloq tugmalar -->
          <div class="qs-quick">
            <button type="button" class="qs-quick-btn" @click="qarzDollarAmount = 0; emitMultiPayment()" :class="{ active: qarzDollarAmount === 0 }">
              0 $
            </button>
            <button v-for="n in [50, 100, 150, 200, 500]" :key="n" type="button" class="qs-quick-btn"
              @click="setQarzDollarRound(n)"
              :class="{ active: qarzDollarAmount === Math.min(n, maxQarzDollar) && n <= maxQarzDollar }"
              :disabled="n > maxQarzDollar"
            >
              {{ n }} $
            </button>
            <button type="button" class="qs-quick-btn qs-max" @click="setQarzDollarMax" v-if="maxQarzDollar > 0">
              MAX ({{ maxQarzDollar }} $)
            </button>
          </div>

          <!-- Custom dollar qarz input -->
          <div class="qs-input-row">
            <span class="qs-icon">💲</span>
            <span class="qs-name">Qarz dollarda:</span>
            <input
              type="number"
              min="0"
              :max="maxQarzDollar"
              step="1"
              :value="qarzDollarAmount"
              @input="onQarzDollarChange($event.target.value)"
              class="qs-input"
              placeholder="0"
            />
            <small class="qs-unit">$</small>
          </div>

          <!-- Hisob-kitob: dollar va so'm taqsimoti -->
          <div class="qs-breakdown">
            <div class="qs-row" v-if="qarzDollarAmount > 0">
              <span>💲 Dollar qarz:</span>
              <strong class="text-info">{{ qarzDollarAmount }} $ ≈ {{ formatSumma(qarzDollarInSum) }} so'm</strong>
            </div>
            <div class="qs-row qs-final">
              <span>💵 So'm qarz:</span>
              <strong class="text-danger">{{ formatSumma(qarzSumRemaining) }} so'm</strong>
            </div>
          </div>
        </div>

        <!-- Qisqacha tezkor: 100% qarz -->
        <button
          v-if="totalSum > 0"
          type="button"
          class="full-debt-btn mt-2"
          @click="setFullDebt"
          :class="{ active: totalPaidSum === 0 }"
        >
          ⏳ Hammasi qarzga (to'lov yo'q)
        </button>

        <!-- Qarz muddati + izoh — qarzga qoladigan summa bo'lsa ko'rsatamiz -->
        <div v-if="qarzAmount > 0 && totalSum > 0" class="debt-due-block mt-2">
          <div class="debt-due-title">Qarz muddati</div>
          <div class="dd-row">
            <input
              type="number"
              min="1"
              max="31"
              placeholder="Kun"
              :value="debtDay"
              @input="onDueChanged('day', $event)"
              class="dd-input"
            />
            <span class="dd-sep">/</span>
            <input
              type="number"
              min="1"
              max="12"
              placeholder="Oy"
              :value="debtMonth"
              @input="onDueChanged('month', $event)"
              class="dd-input"
            />
            <span class="dd-year">/ {{ currentYear }}</span>
          </div>
          <input
            type="text"
            :value="form.debt_comment || ''"
            @input="updateForm('debt_comment', $event)"
            placeholder="Izoh (ixtiyoriy)"
            class="dd-comment mt-1"
          />
        </div>
      </div>

      <!-- Mijoz qarzi bo'lsa -->
      <div v-if="clientDebt > 0" class="debt-section mt-3 p-3 rounded-3 border border-danger border-opacity-25">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <span class="fw-bold text-danger small">💳 Mijoz qarzi:</span>
          <span class="fw-bolder text-danger">{{ formatSumma(clientDebt) }} so'm</span>
        </div>
        <select
          v-model.number="selectedDebtPayTypeId"
          :class="['pay-select', 'pay-' + payClass(selectedDebtPayTypeId), 'mb-2']"
        >
          <option v-for="pt in saleTypes" :key="pt.id" :value="pt.id">{{ pt.name }}</option>
        </select>
        <input
          type="number"
          v-model.number="debtPayAmount"
          :class="['pay-input', 'pay-' + payClass(selectedDebtPayTypeId), 'mb-2']"
          placeholder="0"
          min="0"
        >
        <button
          @click="payDebt"
          :disabled="Number(debtPayAmount) <= 0 || debtLoading"
          class="btn btn-sm btn-danger w-100 fw-bold"
        >
          <span v-if="debtLoading" class="spinner-border spinner-border-sm me-1"></span>
          Qarzni To'lash ({{ formatSumma(debtPayAmount) }} so'm)
        </button>
      </div>

      <!-- Sotishni yakunlash tugmasi -->
      <button
        @click="$emit('submit')"
        :disabled="loading"
        class="btn btn-checkout w-100 py-3 rounded-3 shadow mt-3"
      >
        <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
        SOTISHNI YAKUNLASH
      </button>

    </div>
  </div>
</template>

<script>
export default {
  name: 'SaleCartSidebar',
  props: {
    cart:          { type: Array,   default: () => [] },
    form:          { type: Object,  required: true },
    saleType:      { type: String,  default: 'chakana' },
    totalSum:      { type: Number,  default: 0 },
    productSumma:  { type: Number,  default: 0 },
    serviceSumma:  { type: Number,  default: 0 },
    paidTotal:     { type: Number,  default: 0 },
    masterPercent: { type: Number,  default: 0 },
    masterSumma:   { type: Number,  default: 0 },
    loading:       { type: Boolean, default: false },
    clientDebt:    { type: Number,  default: 0 },
  },
  emits: [
    'update:form',
    'remove-item', 'increase-qty', 'decrease-qty',
    'update-price',
    'toggle-price-lock',
    'clear-all', 'clear-services',
    'pay-debt', 'submit',
  ],
  data() {
    return {
      debtLoading: false,
      payTypes: [],
      // Multi-payment: { pay_type_id: amount } — har turda alohida summa
      paymentByType: {},
      // Joriy bozor kursi (DB'dagi oxirgi yozuv yoki form.dollar_rate)
      apiDollarRate: 0,
      // Mijozga qarz qoldirilganda — qancha qismi $ da, qolgani so'mda
      qarzDollarAmount: 0,
      selectedDebtPayTypeId: null,
      debtPayAmount: 0,
      currentYear: new Date().getFullYear(),
    };
  },
  computed: {
    hasServices() { return this.cart.some(i => i.type === 'service'); },
    hasProducts()  { return this.cart.some(i => i.type === 'product'); },
    debtDay() {
      const ts = Number(this.form.debt_due_date || 0);
      return ts > 0 ? new Date(ts * 1000).getDate() : '';
    },
    debtMonth() {
      const ts = Number(this.form.debt_due_date || 0);
      return ts > 0 ? new Date(ts * 1000).getMonth() + 1 : '';
    },
    saleTypes() {
      // Eski mantiq — qarzni to'lash uchun saqlandi (dollar va shot'ni chiqarib tashlaydi)
      return this.payTypes.filter(p => Number(p.id) !== 3 && Number(p.id) !== 4);
    },

    // Dollar pay_type'ni nom orqali topamiz (id=3 ga ham fallback)
    dollarPayTypeId() {
      if (!this.payTypes.length) return 3;
      const found = this.payTypes.find(p => {
        const n = String(p.name || '').toLowerCase();
        return n.includes('dollar') || n.includes('usd') || n.includes('$') || n.startsWith('доллар');
      });
      return found ? found.id : 3;
    },

    // Joriy dollar kursi: form.dollar_rate > apiDollarRate > 1
    currentRate() {
      const r1 = Number(this.form.dollar_rate || 0);
      if (r1 > 1) return r1;
      const r2 = Number(this.apiDollarRate || 0);
      return r2 > 1 ? r2 : 1;
    },

    // Qarz dollar so'm ekvivalenti
    qarzDollarInSum() {
      return Number(this.qarzDollarAmount || 0) * this.currentRate;
    },

    // Qoldiq qarz so'mda (umumiy qarz - dollar qarz so'm ekvivalenti)
    qarzSumRemaining() {
      return Math.max(0, this.qarzAmount - this.qarzDollarInSum);
    },

    // Qarz dollar uchun maksimal qiymat (joriy qarzni dollarga aylantirilgani)
    maxQarzDollar() {
      if (this.currentRate <= 1) return 0;
      return Math.floor(this.qarzAmount / this.currentRate);
    },

    // Dollar to'lov summasi (USD)
    dollarTotal() {
      return Number(this.paymentByType[this.dollarPayTypeId] || 0);
    },

    // Dollar to'lovning so'm ekvivalenti
    dollarInSum() {
      return this.dollarTotal * this.currentRate;
    },

    // Jami so'mda to'lanmoqda (so'mlilar + dollar*kurs)
    totalPaidSum() {
      let total = 0;
      for (const id of Object.keys(this.paymentByType)) {
        const amount = Number(this.paymentByType[id] || 0);
        if (Number(id) === this.dollarPayTypeId) {
          total += amount * this.currentRate;
        } else {
          total += amount;
        }
      }
      return total;
    },

    // Qoldiq qarz: total - to'langan
    qarzAmount() {
      return (Number(this.totalSum) || 0) - this.totalPaidSum;
    },

    overpaid() {
      return this.qarzAmount < 0;
    },

    // Pay type ikon
    payTypeIcon() {
      return (id, name) => {
        const n = String(name || '').toLowerCase();
        if (id === this.dollarPayTypeId) return '💲';
        if (n.includes('plast') || n.includes('пласт') || n.includes('card')) return '💳';
        if (n.includes('shot')   || n.includes('шот')   || n.includes('перевод')) return '📲';
        if (n.includes('naqd')   || n.includes('нақд')  || n.includes('cash'))   return '💵';
        return '💰';
      };
    },
  },
  methods: {
    formatSumma(s) { return Number(s || 0).toLocaleString(); },
    serviceItemPrice(item) {
      if (this.saleType === 'optom') {
        return Number(item.labor_price_optom || 0) + Number(item.redo_price_optom || 0);
      }
      return Number(item.labor_price_chakana || 0) + Number(item.redo_price_chakana || 0);
    },
    payClass(id) {
      if (Number(id) === 1) return 'naqd';
      return 'plastic';
    },
    updateForm(key, e) {
      // matnli maydonlar uchun (debt_comment) Number qilmaymiz
      const textKeys = ['debt_comment', 'comment', 'car_number'];
      const raw = e.target.value;
      const val = textKeys.includes(key) ? String(raw || '') : (Number(raw) || 0);
      this.$emit('update:form', { ...this.form, [key]: val });
    },
    onDueChanged(which, e) {
      const v = parseInt(e.target.value, 10);
      const cur = Number(this.form.debt_due_date || 0);
      let day, month;
      if (cur > 0) {
        const d = new Date(cur * 1000);
        day = d.getDate();
        month = d.getMonth() + 1;
      } else {
        day = 1;
        month = new Date().getMonth() + 1;
      }
      if (which === 'day') day = v;
      else month = v;
      if (!day || day < 1 || day > 31 || !month || month < 1 || month > 12) {
        // Incomplete — hali yozmaymiz
        return;
      }
      const dt = new Date(this.currentYear, month - 1, day, 23, 59, 59);
      const ts = Math.floor(dt.getTime() / 1000);
      this.$emit('update:form', { ...this.form, debt_due_date: ts });
    },
    onPriceEdit(item, e) {
      const v = Number(e.target.value);
      if (Number.isFinite(v) && v >= 0) {
        this.$emit('update-price', { item, price: v });
      }
    },
    onTogglePriceLock(item, e) {
      this.$emit('toggle-price-lock', { item, unlocked: !!e.target.checked });
    },
    onDiscountPercentEdit(item, e) {
      let pct = Number(e.target.value);
      if (!Number.isFinite(pct) || pct < 0) pct = 0;
      if (pct > 100) pct = 100;
      this.$emit('update-discount-percent', { item, percent: pct });
    },
    async fetchDollarRate() {
      try {
        const axios = (await import('axios')).default;
        // Oxirgi kurs
        const res = await axios.get('api/v1/dollar-exchange-rate/last');
        const r = Number((res.data && res.data.rate) || 0);
        if (r > 1) {
          this.apiDollarRate = r;
          // Agar form.dollar_rate hali o'rnatilmagan bo'lsa, formaga ham qo'yamiz
          if (!this.form.dollar_rate || Number(this.form.dollar_rate) <= 1) {
            this.$emit('update:form', { ...this.form, dollar_rate: r });
          }
        }
      } catch (e) {
        // Server javob bermasa default 1 (ko'rsatish to'g'ri ishlamaydi, lekin xato chiqmaydi)
      }
    },
    async fetchPayTypes() {
      try {
        const axios = (await import('axios')).default;
        const res = await axios.get('api/v1/pay-type/');
        this.payTypes = (res.data || []).sort((a, b) => a.id - b.id);
      } catch (e) {
        this.payTypes = [
          { id: 1, name: 'Naqd' },
          { id: 2, name: 'Plastik' },
          { id: 3, name: 'Dollar' },
          { id: 4, name: 'Shot' },
        ];
      }
      // Qarzni to'lash uchun default — birinchi so'mli pay_type
      if (!this.selectedDebtPayTypeId && this.saleTypes.length) {
        this.selectedDebtPayTypeId = this.saleTypes[0].id;
      }
      // Default holatda paymentByType bo'sh — foydalanuvchi qaysi turga to'lashni o'zi tanlaydi
      // Avtomatik formani emit qilamiz (qarz = total)
      this.emitMultiPayment();
    },

    // Multi-payment ma'lumotini formaga uzatish
    emitMultiPayment() {
      // Har pay_type'dan kelgan summani tegishli backend maydoniga taqsimlaymiz:
      //   nomida 'plast/card' bo'lsa → plastic_summa
      //   'shot/перевод' bo'lsa → shot_summa
      //   boshqa (naqd va h.k.) → cash_summa
      //   Dollar uchun → dollar_summa (USD), va so'm ekvivalenti paid_summa'ga qo'shiladi
      let cash_summa = 0, plastic_summa = 0, shot_summa = 0;
      let primaryType = 1;
      let maxAmount = 0;

      for (const p of this.payTypes) {
        const amount = Number(this.paymentByType[p.id] || 0);
        if (amount <= 0 || p.id === this.dollarPayTypeId) continue;
        const n = String(p.name || '').toLowerCase();
        if (n.includes('plast') || n.includes('пласт') || n.includes('card')) plastic_summa += amount;
        else if (n.includes('shot') || n.includes('шот') || n.includes('перевод')) shot_summa += amount;
        else cash_summa += amount;
        if (amount > maxAmount) {
          maxAmount = amount;
          primaryType = p.id;
        }
      }

      // Dollarni paid_summa ga so'm ekvivalent qilib qo'shamiz
      const dollarSum = this.dollarInSum;
      if (dollarSum > maxAmount) {
        primaryType = this.dollarPayTypeId;
      }

      const totalPaidLocal = cash_summa + plastic_summa + shot_summa + dollarSum;
      const total = Number(this.totalSum) || 0;
      const qarz = Math.max(0, total - totalPaidLocal);

      // qarz_dollar_amount qarzdan oshib ketmasligi uchun klamp
      const maxD = this.currentRate > 1 ? Math.floor(qarz / this.currentRate) : 0;
      if (this.qarzDollarAmount > maxD) this.qarzDollarAmount = maxD;

      const qarzDollarSum = Number(this.qarzDollarAmount || 0) * this.currentRate;
      const qarzSumOnly = Math.max(0, qarz - qarzDollarSum);

      // YANGI: dinamik payments array (har pay_type alohida yozuv)
      // Backend bu massivni service_sale_payment jadvaliga saqlaydi.
      const paymentsArr = [];
      for (const pt of this.payTypes) {
        const amount = Number(this.paymentByType[pt.id] || 0);
        if (amount > 0) {
          paymentsArr.push({
            pay_type_id: pt.id,
            summa: amount,           // USD birligida agar dollar, aks holda so'mda
          });
        }
      }

      this.$emit('update:form', {
        ...this.form,
        cash_summa,                            // backward compat
        plastic_summa,
        shot_summa,
        dollar_summa: this.dollarTotal,        // USD birligida (to'lov)
        paid_summa: totalPaidLocal,             // hammasi so'mda
        qarz_summa: qarzSumOnly,                // qoldiq qarz so'mda
        qarz_dollar_summa: Number(this.qarzDollarAmount || 0), // qarz USD birligida
        pay_type_id: primaryType,
        payments: paymentsArr,                  // YANGI: dinamik to'lov turlari
      });
    },

    onQarzDollarChange(value) {
      // Faqat butun son va qarzdan oshmasligini ta'minlaymiz
      let v = Math.max(0, Math.floor(Number(value) || 0));
      if (v > this.maxQarzDollar) v = this.maxQarzDollar;
      this.qarzDollarAmount = v;
      this.emitMultiPayment();
    },

    setQarzDollarMax() {
      // Hammasini dollar qarzga (yumaloq pastga)
      this.qarzDollarAmount = this.maxQarzDollar;
      this.emitMultiPayment();
    },

    setQarzDollarRound(step) {
      // Yumaloq qiymat tanlash: 50, 100, 200 va h.k.
      const max = this.maxQarzDollar;
      const v = Math.min(step, max);
      this.qarzDollarAmount = v;
      this.emitMultiPayment();
    },

    onPaymentChange(payTypeId, value) {
      let v = Math.max(0, Number(value) || 0);
      // Dollar to'lovni butun songa yumalaymiz (50, 100, 150, ...)
      if (Number(payTypeId) === this.dollarPayTypeId) {
        v = Math.floor(v);
      }
      this.$set(this.paymentByType, payTypeId, v);
      this.emitMultiPayment();
    },

    fillRemaining(payTypeId) {
      // Qoldiq qarzni shu turga to'ldirish
      const total = Number(this.totalSum) || 0;
      // Boshqa to'lovlar yig'indisini olamiz
      let other = 0;
      for (const id of Object.keys(this.paymentByType)) {
        if (Number(id) === Number(payTypeId)) continue;
        const a = Number(this.paymentByType[id] || 0);
        if (Number(id) === this.dollarPayTypeId) other += a * this.currentRate;
        else other += a;
      }
      const remainingSum = Math.max(0, total - other);
      let value = remainingSum;
      // Dollar uchun so'mni dollarga aylantirib BUTUN songa yumalaymiz (pastga, ortiqcha bo'lmasin)
      if (Number(payTypeId) === this.dollarPayTypeId) {
        value = this.currentRate > 1 ? Math.floor(remainingSum / this.currentRate) : 0;
      }
      this.$set(this.paymentByType, payTypeId, value);
      this.emitMultiPayment();
    },

    autofillCash() {
      // Hammasini tozalab, faqat naqdga to'liq summa
      this.paymentByType = {};
      const naqd = this.payTypes.find(p => {
        const n = String(p.name || '').toLowerCase();
        return n.includes('naqd') || n.includes('cash');
      }) || this.saleTypes[0];
      if (naqd) {
        this.$set(this.paymentByType, naqd.id, Number(this.totalSum) || 0);
      }
      this.emitMultiPayment();
    },

    setFullDebt() {
      // Hammasini tozalash — to'liq qarz
      this.paymentByType = {};
      this.emitMultiPayment();
    },

    payDebt() {
      const amount = Number(this.debtPayAmount) || 0;
      if (amount <= 0) return;
      const typeId = Number(this.selectedDebtPayTypeId);
      let cash = 0, plastic = 0, shot = 0;
      if (typeId === 1)      cash = amount;
      else if (typeId === 4) shot = amount;
      else                   plastic = amount;
      this.$emit('pay-debt', { cash, plastic, shot });
      this.debtPayAmount = 0;
    },
  },
  mounted() {
    this.fetchPayTypes();
    this.fetchDollarRate();
  },
  watch: {
    totalSum() {
      // totalSum o'zgarsa — qarz hisobini qayta uzatamiz
      this.emitMultiPayment();
    },
    'form.dollar_rate'() {
      // Dollar kursi o'zgarsa — paid_summa'ni qayta hisoblaymiz
      this.emitMultiPayment();
    },
  },
};
</script>

<style scoped>
/* Cart items */
.cart-row {
  background: #f8fafc;
  border-radius: 10px;
  padding: 10px;
}
.service-row {
  background: #f5f0ff;
  border-left: 3px solid #a142f4;
}
.service-product-row {
  background: #f0f4ff;
  border-left: 3px solid #60a5fa;
  padding: 6px 10px;
  margin-left: 12px;
}
.quantity-control {
  display: flex;
  align-items: center;
  background: white;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}
.qty-btn {
  border: none;
  background: none;
  padding: 2px 10px;
  font-weight: bold;
  cursor: pointer;
}
.qty-val {
  padding: 0 8px;
  font-weight: 600;
}
.price-edit-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
}
.price-input {
  flex: 1;
  height: 26px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 0.78rem;
  font-weight: 600;
  background: #f8fafc;
  outline: none;
  max-width: 120px;
}
.price-input:focus { border-color: #5850ec; background: #fff; }
.price-original {
  text-decoration: line-through;
  color: #94a3b8;
  font-size: 0.7rem;
}
.price-expensive {
  color: #16a34a;
  font-weight: 700;
  font-size: 0.7rem;
}
.price-input.locked {
  background: #f1f5f9;
  color: #64748b;
  cursor: not-allowed;
  border-style: dashed;
}

/* Per-product chegirma foizi qatori */
.discount-pct-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  background: #fff7ed;
  border: 1px dashed #fb923c;
  border-radius: 6px;
}
.dp-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background: #fb923c;
  color: white;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 800;
}
.dp-input {
  width: 50px;
  padding: 2px 6px;
  font-size: 12px;
  border: 1px solid #fdba74;
  border-radius: 4px;
  text-align: center;
  font-weight: 700;
  color: #c2410c;
  background: white;
}
.dp-input:focus { outline: none; border-color: #ea580c; box-shadow: 0 0 0 2px rgba(251, 146, 60, 0.2); }
.dp-saved {
  margin-left: auto;
  font-size: 11px;
  font-weight: 700;
  color: #c2410c;
}

/* Switch */
.price-switch {
  position: relative;
  display: inline-block;
  width: 32px;
  height: 18px;
  flex-shrink: 0;
}
.price-switch input {
  opacity: 0;
  width: 0; height: 0;
}
.ps-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #cbd5e1;
  border-radius: 18px;
  transition: 0.2s;
}
.ps-slider::before {
  content: "";
  position: absolute;
  height: 14px; width: 14px;
  left: 2px; top: 2px;
  background: white;
  border-radius: 50%;
  transition: 0.2s;
  box-shadow: 0 1px 2px rgba(0,0,0,0.2);
}
.price-switch input:checked + .ps-slider { background: #f97316; }
.price-switch input:checked + .ps-slider::before { transform: translateX(14px); }
.debt-due-block {
  background: #fff7ed;
  border: 1px solid #fdba74;
  border-radius: 10px;
  padding: 8px 10px;
}
.debt-due-title {
  font-size: 0.72rem;
  font-weight: 700;
  color: #9a3412;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 4px;
}
.dd-row { display: flex; align-items: center; gap: 6px; }
.dd-input {
  flex: 1;
  height: 30px;
  border: 1px solid #fdba74;
  border-radius: 6px;
  padding: 0 8px;
  font-size: 0.8rem;
  font-weight: 600;
  text-align: center;
  background: white;
  outline: none;
  min-width: 0;
}
.dd-input:focus { border-color: #f97316; }
.dd-sep { color: #9a3412; font-weight: 700; }
.dd-year { font-weight: 700; color: #9a3412; font-size: 0.82rem; white-space: nowrap; }
.dd-comment {
  width: 100%;
  height: 30px;
  border: 1px solid #fdba74;
  border-radius: 6px;
  padding: 0 8px;
  font-size: 0.78rem;
  background: white;
  outline: none;
}
.dd-comment:focus { border-color: #f97316; }
.btn-close-sm {
  background: none;
  border: none;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  color: #94a3b8;
}
.btn-close-sm:hover { color: #ef4444; }

/* Summary inputs */
.summary-input-field {
  width: 120px;
  text-align: right;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 4px 8px;
  font-weight: 700;
  font-size: 0.9rem;
}
.summary-input-field:focus {
  border-color: #3b82f6;
  outline: none;
}

/* Payment inputs */
.tiny-label {
  font-size: 0.7rem;
  color: #94a3b8;
  font-weight: 600;
  display: block;
  margin-bottom: 3px;
}
.pay-input {
  width: 100%;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px;
  font-weight: 700;
  font-size: 0.85rem;
  text-align: center;
  transition: 0.2s;
}
.pay-input:focus { outline: none; }
.pay-naqd:focus    { border-color: #22c55e; box-shadow: 0 0 0 3px rgba(34,197,94,0.15); }
.pay-plastic:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.15); }
.pay-shot:focus    { border-color: #f59e0b; box-shadow: 0 0 0 3px rgba(245,158,11,0.15); }

/* Pay type select (alohida qator) */
.pay-select {
  display: block;
  width: 100%;
  height: 40px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  padding: 0 10px;
  font-weight: 700;
  font-size: 0.85rem;
  background: white;
  cursor: pointer;
  outline: none;
  transition: 0.2s;
}
.pay-select:focus { outline: none; }
.pay-select.pay-naqd    { border-color: #22c55e; color: #15803d; }
.pay-select.pay-plastic { border-color: #3b82f6; color: #1d4ed8; }
.pay-select.pay-shot    { border-color: #f59e0b; color: #b45309; }

/* Summa + To'liq tugmasi qatori (select dan keyin) */
.pay-amount-row {
  display: flex;
  align-items: stretch;
  gap: 6px;
}
.pay-amount-row .pay-input { flex: 1; min-width: 0; }
.fill-btn {
  border: 2px solid #64748b;
  background: white;
  color: #334155;
  font-weight: 700;
  font-size: 0.78rem;
  border-radius: 10px;
  padding: 0 14px;
  cursor: pointer;
  transition: 0.15s;
  white-space: nowrap;
}
.fill-btn:hover:not(:disabled) { background: #64748b; color: white; }
.fill-btn:disabled { opacity: 0.45; cursor: not-allowed; }

@media (max-width: 560px) {
  .pay-input { padding: 6px; font-size: 0.8rem; border-radius: 8px; }
  .tiny-label { font-size: 0.65rem; }
  .pay-select { height: 36px; font-size: 0.8rem; padding: 0 8px; }
  .fill-btn { padding: 0 10px; font-size: 0.72rem; }
}

/* Qisman qarz toggle tugmasi */
.partial-row { display: flex; }
.partial-btn {
  flex: 1;
  border: 2px dashed #f59e0b;
  background: #fffbeb;
  color: #b45309;
  font-weight: 700;
  font-size: 0.78rem;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: 0.15s;
}
.partial-btn:hover { background: #fef3c7; }
.partial-btn.active {
  background: #fdba74;
  border-style: solid;
  color: #7c2d12;
}
.partial-icon { font-size: 1rem; }

/* Qisman to'lov bloki */
.partial-block {
  background: #fff7ed;
  border: 1px solid #fdba74;
  border-radius: 10px;
  padding: 10px;
}
.partial-input-row .pay-input {
  font-size: 0.95rem;
  height: 38px;
}
.partial-status {
  background: white;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 0.82rem;
}
.ps-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 0;
}

/* Status badges */
.status-badge {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 0.82rem;
  font-weight: 600;
}
.nasiya-badge {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #c2410c;
}
.paid-badge {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #15803d;
}

/* Debt section */
.debt-section {
  background: #fff5f5;
}

/* Checkout btn */
.btn-checkout {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  font-weight: 800;
  letter-spacing: 1px;
  font-size: 0.85rem;
}
.btn-checkout:disabled { opacity: 0.6; }

/* Badge colors */
.bg-purple { background-color: #a142f4 !important; }
.text-purple { color: #a142f4 !important; }

/* ───── MULTI-PAYMENT GRID ───── */
.multi-pay-grid {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.mp-row {
  display: grid;
  grid-template-columns: 28px 1fr 130px 70px;
  align-items: center;
  gap: 8px;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px 10px;
  transition: 0.2s;
}

.mp-row:hover { border-color: #cbd5e1; }

.mp-row.mp-active {
  background: #faf5ff;
  border-color: #a142f4;
  box-shadow: 0 2px 6px rgba(161, 66, 244, 0.08);
}

.mp-row.mp-dollar.mp-active {
  background: #f0fdf4;
  border-color: #16a34a;
  box-shadow: 0 2px 6px rgba(22, 163, 74, 0.08);
}

.mp-icon {
  font-size: 18px;
  text-align: center;
}

.mp-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.mp-name {
  font-weight: 700;
  font-size: 13px;
  color: #1e293b;
}

.mp-unit {
  font-size: 10px;
  color: #94a3b8;
  font-weight: 600;
  text-transform: uppercase;
}

.mp-input {
  width: 100%;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 14px;
  font-weight: 700;
  font-family: 'Inter', system-ui, sans-serif;
  color: #1e293b;
  outline: none;
  text-align: right;
  transition: 0.15s;
}

.mp-input:focus {
  border-color: #a142f4;
  box-shadow: 0 0 0 3px rgba(161, 66, 244, 0.08);
}

.mp-row.mp-dollar .mp-input:focus {
  border-color: #16a34a;
  box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.08);
}

.mp-fill {
  background: #f1f5f9;
  border: none;
  padding: 6px 8px;
  border-radius: 7px;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  cursor: pointer;
  transition: 0.15s;
}

.mp-fill:hover {
  background: #a142f4;
  color: white;
}

/* Multi-payment status box */
.multi-pay-status {
  margin-top: 10px;
  padding: 10px 12px;
  background: #f8fafc;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.mps-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: #475569;
}

.mps-row strong { font-family: 'Inter', system-ui, sans-serif; }

.mps-row.mps-debt {
  border-top: 1px dashed #cbd5e1;
  padding-top: 6px;
  margin-top: 4px;
  font-size: 14px;
  font-weight: 700;
  color: #b91c1c;
}

.mps-row.mps-debt strong { color: #b91c1c; }

.mps-row.mps-overpay {
  color: #b45309;
}

.mps-row.mps-overpay strong { color: #b45309; }

.mps-row.mps-skip {
  color: #15803d;
  font-weight: 700;
  justify-content: center;
}

/* Tezkor tugmalar */
.quick-btn {
  background: #faf5ff;
  border: 1px solid #e9d5ff;
  color: #7c3aed;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: 0.15s;
}

.quick-btn:hover {
  background: #a142f4;
  color: white;
  border-color: #a142f4;
}

.full-debt-btn {
  width: 100%;
  background: #fff7ed;
  border: 1.5px solid #fed7aa;
  color: #c2410c;
  padding: 9px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  transition: 0.15s;
}

.full-debt-btn:hover {
  background: #fed7aa;
}

.full-debt-btn.active {
  background: #fed7aa;
  border-color: #c2410c;
}

.text-info { color: #0284c7 !important; }

/* ───── QARZ TURI (DOLLAR/SO'M SPLIT) ───── */
.qarz-split-block {
  background: linear-gradient(135deg, #fff7ed 0%, #fef3c7 100%);
  border: 1.5px solid #fde68a;
  border-radius: 12px;
  padding: 12px;
}

.qs-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-weight: 800;
  font-size: 13px;
  color: #92400e;
  margin-bottom: 10px;
  letter-spacing: 0.3px;
}

.qs-header small {
  font-weight: 600;
  font-size: 11px;
  color: #b45309;
}

.qs-quick {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 10px;
}

.qs-quick-btn {
  background: white;
  border: 1.5px solid #fed7aa;
  color: #c2410c;
  padding: 5px 10px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 800;
  cursor: pointer;
  transition: 0.15s;
  min-width: 44px;
}

.qs-quick-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.qs-quick-btn:not(:disabled):hover {
  background: #fed7aa;
  border-color: #c2410c;
}

.qs-quick-btn.active {
  background: #c2410c;
  color: white;
  border-color: #c2410c;
  box-shadow: 0 2px 6px rgba(194, 65, 12, 0.25);
}

.qs-quick-btn.qs-max {
  background: #16a34a;
  color: white;
  border-color: #16a34a;
}

.qs-quick-btn.qs-max:hover {
  background: #15803d;
}

.qs-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  border: 1.5px solid #fed7aa;
  border-radius: 10px;
  padding: 6px 10px;
  margin-bottom: 8px;
}

.qs-input-row:focus-within {
  border-color: #c2410c;
  box-shadow: 0 0 0 3px rgba(194, 65, 12, 0.08);
}

.qs-icon { font-size: 16px; }

.qs-name {
  font-size: 12px;
  font-weight: 700;
  color: #92400e;
  flex: 1;
}

.qs-input {
  width: 80px;
  background: transparent;
  border: none;
  text-align: right;
  font-size: 15px;
  font-weight: 800;
  color: #1e293b;
  outline: none;
  font-family: 'Inter', system-ui, sans-serif;
}

.qs-unit {
  font-weight: 700;
  font-size: 14px;
  color: #c2410c;
}

.qs-breakdown {
  background: white;
  border-radius: 8px;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.qs-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #475569;
}

.qs-row strong {
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 13px;
}

.qs-row.qs-final {
  border-top: 1px dashed #fed7aa;
  padding-top: 4px;
  margin-top: 2px;
  font-weight: 700;
}
</style>
